import { useEffect, useRef, useState } from "react";

type RecognitionEvent = Event & {
  resultIndex: number;
  results: {
    [index: number]: {
      [index: number]: { transcript: string };
      isFinal: boolean;
    };
    length: number;
  };
};

type RecognitionErrorEvent = Event & {
  error: string;
};

type RecognitionInstance = EventTarget & {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onstart: (() => void) | null;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
};

type RecognitionConstructor = new () => RecognitionInstance;

declare global {
  interface Window {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  }
}

export function useSpeechRecognition(onText: (text: string) => void) {
  const recognitionRef = useRef<RecognitionInstance | null>(null);
  const shouldListenRef = useRef(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState("");
  const Recognition =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : undefined;

  useEffect(() => {
    if (!Recognition) return;
    const recognition = new Recognition();
    recognition.lang = "zh-CN";
    recognition.interimResults = false;
    recognition.continuous = true;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = Array.from(
        { length: event.results.length - event.resultIndex },
        (_, offset) => event.resultIndex + offset
      )
        .filter((index) => event.results[index].isFinal)
        .map((index) => event.results[index][0]?.transcript || "")
        .join("");
      if (transcript) onText(transcript);
    };
    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        setError(speechErrorMessage(event.error));
      }
      shouldListenRef.current = false;
      setIsListening(false);
    };
    recognition.onend = () => {
      setIsListening(false);
      if (shouldListenRef.current) {
        try {
          recognition.start();
        } catch {
          shouldListenRef.current = false;
          setError("麦克风正忙，请松开后再试。");
        }
      }
    };
    recognitionRef.current = recognition;

    return () => {
      shouldListenRef.current = false;
      recognition.stop();
      recognitionRef.current = null;
    };
  }, [Recognition, onText]);

  const start = () => {
    const recognition = recognitionRef.current;
    if (!recognition || shouldListenRef.current) return;
    shouldListenRef.current = true;
    setError("");
    try {
      recognition.start();
    } catch {
      shouldListenRef.current = false;
      setIsListening(false);
      setError("麦克风正忙，请松开后再试。");
    }
  };

  const stop = () => {
    shouldListenRef.current = false;
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch {
      // 识别器尚未完全启动时松开，不需要向用户报错。
    } finally {
      setIsListening(false);
    }
  };

  return {
    isSupported: Boolean(Recognition),
    isListening,
    error,
    start,
    stop
  };
}

function speechErrorMessage(error: string) {
  if (error === "not-allowed" || error === "service-not-allowed") {
    return "浏览器没有麦克风权限，请在地址栏设置中允许。";
  }
  if (error === "no-speech") return "没听清，再说一遍试试。";
  if (error === "network") return "语音识别服务暂时不可用。";
  return "语音识别没有成功，请重试。";
}
